import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Eye, EyeOff, Loader2, LogIn } from "lucide-react";

import { ApiError } from "../../lib/api";
import { loginRequest } from "./api";
import { useAuth } from "./useAuth";

const schema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Requerido"),
});

type FormValues = z.infer<typeof schema>;

interface LocationState {
  from?: string;
}

type Node = { x: number; y: number; size: number; accent: boolean };

// Constelación de comités SSR — posiciones fijas (% del viewBox 0-100) para
// evitar layout shift y mantener la composición estable entre renders.
// Los nodos `accent` se pintan en cyan; el resto en blanco apagado.
const NODES: ReadonlyArray<Node> = [
  { x: 14, y: 22, size: 5, accent: false },
  { x: 28, y: 12, size: 4, accent: false },
  { x: 24, y: 38, size: 9, accent: true },
  { x: 46, y: 26, size: 5, accent: false },
  { x: 42, y: 52, size: 7, accent: false },
  { x: 60, y: 16, size: 4, accent: false },
  { x: 68, y: 44, size: 10, accent: true },
  { x: 56, y: 64, size: 6, accent: false },
  { x: 80, y: 28, size: 5, accent: false },
  { x: 86, y: 60, size: 7, accent: false },
  { x: 16, y: 58, size: 5, accent: false },
  { x: 12, y: 78, size: 4, accent: false },
  { x: 36, y: 80, size: 8, accent: true },
  { x: 54, y: 86, size: 5, accent: false },
  { x: 76, y: 80, size: 6, accent: false },
  { x: 92, y: 86, size: 4, accent: false },
];

const EDGES: ReadonlyArray<readonly [number, number]> = [
  [0, 1], [0, 2], [1, 3], [2, 3], [2, 4],
  [3, 5], [4, 6], [5, 6], [6, 7], [6, 8],
  [8, 9], [7, 12], [10, 11], [11, 12], [12, 13],
  [13, 14], [14, 15], [9, 14], [10, 2], [4, 7],
];

function BrandBackdrop(): JSX.Element {
  return (
    <>
      <div aria-hidden className="absolute inset-0 overflow-hidden">
        {/* Halo central suave */}
        <div
          className="absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(202, 158, 103, 0.20) 0%, rgba(202, 158, 103, 0.06) 40%, transparent 70%)",
          }}
        />

        {/* Líneas — viewBox 0-100 estirado para que ocupe todo el panel */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
        >
          {EDGES.map(([from, to], i) => {
            const a = NODES[from]!;
            const b = NODES[to]!;
            return (
              <line
                key={i}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="rgb(202, 158, 103)"
                strokeWidth="0.13"
                strokeOpacity="0.35"
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        {/* Nodos posicionados en % para mantener forma circular real */}
        {NODES.map((n, i) => (
          <span
            key={i}
            className="is-node absolute rounded-full"
            style={{
              left: `${n.x}%`,
              top: `${n.y}%`,
              width: `${n.size}px`,
              height: `${n.size}px`,
              transform: "translate(-50%, -50%)",
              background: n.accent ? "rgb(202, 158, 103)" : "rgb(255, 255, 255)",
              boxShadow: n.accent
                ? "0 0 14px rgba(202, 158, 103, 0.7), 0 0 28px rgba(202, 158, 103, 0.35)"
                : "0 0 6px rgba(255, 255, 255, 0.55)",
              animationDelay: `${(i * 0.35) % 4}s`,
              animationDuration: `${4 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes is-node-pulse {
          0%, 100% { opacity: 0.92; }
          50% { opacity: 0.42; }
        }
        @media (prefers-reduced-motion: no-preference) {
          .is-node {
            animation-name: is-node-pulse;
            animation-iteration-count: infinite;
            animation-timing-function: ease-in-out;
          }
        }
      `}</style>
    </>
  );
}

// Movimiento del login (idéntico en las cuatro apps): entrada escalonada
// (fade + leve subida), temblor del error y autocompletado sin el fondo
// azul del navegador. Todo apagado con prefers-reduced-motion.
const LOGIN_CSS = `
  @keyframes ic-in {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes ic-shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }
  @media (prefers-reduced-motion: no-preference) {
    .ic-in { animation: ic-in 0.7s ease-out both; }
    .ic-shake { animation: ic-shake 0.4s ease-in-out; }
  }
  /* Chrome pinta el autocompletado de azul y no deja cambiarlo: se posterga
     su fondo indefinidamente y se fija el color del texto. */
  .ic-input:-webkit-autofill,
  .ic-input:-webkit-autofill:hover,
  .ic-input:-webkit-autofill:focus {
    -webkit-text-fill-color: #1D1A17;
    caret-color: #1D1A17;
    transition: background-color 600000s 0s, color 600000s 0s;
  }
`;

/** Retardo de la entrada escalonada (segundos). */
const entra = (s: number): React.CSSProperties => ({ animationDelay: `${s}s` });

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // Cambia en cada intento fallido: re-monta el aviso para repetir el temblor.
  const [intento, setIntento] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const mutation = useMutation({
    mutationFn: loginRequest,
    onSuccess: (data) => {
      login(data.access_token);
      // Prioridad de redirect post-login:
      //   1) `state.from` (lo deja `ProtectedRoute` cuando interceptó nav)
      //   2) `?next=` query param (lo deja `apiRequest` al detectar 401
      //      en una sesión activa que expiró)
      //   3) home (/)
      const fromState = (location.state as LocationState | null)?.from;
      const nextParam = new URLSearchParams(location.search).get("next");
      const dest = fromState ?? nextParam ?? "/";
      navigate(dest, { replace: true });
    },
    onError: (err) => {
      setIntento((n) => n + 1);
      if (err instanceof ApiError) {
        setServerError(
          err.status === 401 ? "Credenciales inválidas" : err.message,
        );
      } else {
        setServerError(
          "No se pudo conectar al servidor. Verifica tu conexión a internet.",
        );
      }
    },
  });

  const onSubmit = (values: FormValues) => {
    setServerError(null);
    mutation.mutate(values);
  };

  const pending = isSubmitting || mutation.isPending;

  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-2">
      <style>{LOGIN_CSS}</style>
      {/* Columna izquierda — formulario */}
      <div className="flex flex-col justify-between gap-10 px-6 py-10 sm:px-10 sm:py-12 lg:px-16">
        <header className="ic-in flex items-center justify-between gap-4" style={entra(0)}>
          <div className="flex items-center gap-4">
            <img
              src="/clarke-innova-logo.png"
              alt="Clarke Innova"
              width={120}
              height={40}
              className="h-10 w-auto"
            />
            <div className="border-l border-neutral-200 pl-4 leading-tight">
              <div className="font-display text-sm font-semibold text-neutral-900">
                SSR
              </div>
              <div className="text-xs text-neutral-500">Panel administrativo</div>
            </div>
          </div>
          {/* Vuelta al portal del ecosistema (igual en los seis logins). */}
          <a
            href="https://innovaclarke.cl"
            title="Volver a innovaclarke.cl"
            className="group inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded text-xs font-medium text-neutral-500 transition-colors hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform motion-safe:group-hover:-translate-x-0.5" />
            <span className="hidden sm:inline">innovaclarke.cl</span>
            <span className="sm:hidden">Portal</span>
          </a>
        </header>

        <main className="mx-auto flex w-full max-w-sm flex-col gap-7">
          <div className="ic-in" style={entra(0.1)}>
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
              Bienvenido de vuelta
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              Ingresa para gestionar la operación diaria de tu comité.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="ic-in" style={entra(0.2)}>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-neutral-700"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                spellCheck={false}
                disabled={pending}
                placeholder="tu@comite.cl"
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? "email-error" : undefined}
                {...register("email")}
                className="ic-input mt-1 block min-h-11 w-full rounded-md border border-neutral-450 bg-white px-3 py-2 text-base sm:text-sm shadow-sm transition-colors placeholder:text-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
              />
              {errors.email && (
                <p id="email-error" className="mt-1 text-xs text-rose-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="ic-in" style={entra(0.28)}>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-neutral-700"
              >
                Contraseña
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  disabled={pending}
                  placeholder="••••••••"
                  aria-invalid={errors.password ? true : undefined}
                  aria-describedby={errors.password ? "password-error" : undefined}
                  {...register("password")}
                  className="ic-input block min-h-11 w-full rounded-md border border-neutral-450 bg-white px-3 py-2 pr-12 text-base sm:text-sm shadow-sm transition-colors placeholder:text-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={pending}
                  aria-label={
                    showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                  className="absolute inset-y-0 right-0 flex h-11 w-11 items-center justify-center rounded-r-md text-neutral-500 transition-colors hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1 text-xs text-rose-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            {serverError && (
              <div
                key={intento}
                role="alert"
                className="ic-shake rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              style={entra(0.36)}
              className="ic-in group mt-2 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-800 motion-safe:hover:-translate-y-px hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {pending ? (
                <>
                  <Loader2 className="h-4 w-4 motion-safe:animate-spin" />
                  Ingresando…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-0.5" />
                  Ingresar
                </>
              )}
            </button>

            <p className="ic-in pt-1 text-center text-xs text-neutral-500" style={entra(0.44)}>
              ¿Olvidaste tu contraseña? Contacta al administrador del comité.
            </p>
          </form>
        </main>

        <footer className="ic-in text-xs text-neutral-600" style={entra(0.5)}>
          © {new Date().getFullYear()} InnovaClarke · Servicios Sanitarios Rurales
        </footer>
      </div>

      {/* Columna derecha — panel de marca (oculta bajo lg) */}
      <aside
        aria-hidden
        className="relative hidden overflow-hidden bg-noche-900 text-white lg:block"
      >
        <BrandBackdrop />

        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-brand-100/70">
            <span className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-300 shadow-[0_0_8px_rgba(202,158,103,0.7)]" />
              v0.1.0 · operativo
            </span>
            <span>Ley 20.998 · SISS</span>
          </div>

          <div className="ic-in max-w-md space-y-6" style={entra(0.25)}>
            <p className="font-display text-3xl leading-snug text-white/95">
              Tecnología con respaldo contable.
            </p>
            <p className="text-base leading-relaxed text-white/70">
              <span className="text-brand-300">
                La operación diaria de un comité SSR
              </span>{" "}
              sostenida por software hecho para el oficio.
            </p>
            <div className="text-xs uppercase tracking-[0.22em] text-brand-100/60">
              — InnovaClarke
            </div>
          </div>

          <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-brand-100/60">
            <span>InnovaClarke SSR</span>
            <span>innovaclarke.cl</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
